/**
 * assets/sparkle-cursor.js
 * Cursor sparkle trail — hardened production build.
 *
 * Drop-in replacement. No template, Liquid, or markup changes required.
 * Uses the existing <canvas id="sparkles"> element only.
 *
 * Fixes vs. previous version:
 *   - Canvas sized to viewport, not document height (was 15.5MP / ~59MB)
 *   - rAF loop idles out when there is nothing to draw (was infinite)
 *   - Emission throttled and particle count capped (was 10/event, unbounded)
 *   - Particle lifetime floored (was Math.random(), produced immortal particles)
 *
 * Hardening:
 *   - Single-instance guard (file is safe to load more than once)
 *   - Pauses on hidden tab
 *   - Handles 2D context loss/restore
 *   - Delta-time motion, so behaviour is identical at 30 / 60 / 120Hz
 *   - Live prefers-reduced-motion and pointer-capability response
 *   - DPR change handling (browser zoom, monitor change)
 *   - Debounced resize; no layout reads inside the animation loop
 *   - Shopify theme-editor safe (rebuilds on section load/unload)
 *   - Never throws into the page; entry points are guarded
 */
(function () {
  'use strict';

  if (window.__vhSparkleCursorLoaded) return;
  window.__vhSparkleCursorLoaded = true;

  var CANVAS_ID = 'sparkles';

  var CONFIG = {
    emitIntervalMs: 32,   // ~30 emissions/sec ceiling
    perEmit: 3,           // particles per emission
    maxParticles: 150,    // hard ceiling
    spread: 10,           // px jitter around the cursor
    fadeMin: 0.02,        // opacity lost per 60fps frame
    fadeMax: 0.06,
    maxDpr: 2,
    resizeDebounceMs: 150
  };

  var COLORS = ['purple', 'red', 'yellow', 'green', 'blue', 'orange', 'pink', 'white'];

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  function on(target, type, fn, opts) {
    if (target && target.addEventListener) target.addEventListener(type, fn, opts);
  }
  function off(target, type, fn) {
    if (target && target.removeEventListener) target.removeEventListener(type, fn);
  }

  function Sparkles(canvas) {
    this.canvas = canvas;
    this.ctx = null;
    this.particles = [];
    this.running = false;
    this.rafId = null;
    this.destroyed = false;
    this.contextLost = false;
    this.lastEmit = 0;
    this.lastFrame = 0;
    this.w = 0;
    this.h = 0;
    this.dpr = 0;
    this.resizeTimer = null;

    // One colour per page load, matching the previous behaviour.
    this.color = COLORS[Math.floor(Math.random() * COLORS.length)];

    this._onResize = this._onResize.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onContextLost = this._onContextLost.bind(this);
    this._onContextRestored = this._onContextRestored.bind(this);
    this._onMotionChange = this._bindPointer.bind(this);
    this._frame = this._frame.bind(this);

    this._init();
  }

  Sparkles.prototype._init = function () {
    try {
      this.ctx = this.canvas.getContext('2d');
    } catch (e) {
      this.ctx = null;
    }
    if (!this.ctx) return;

    this.canvas.setAttribute('aria-hidden', 'true');
    var s = this.canvas.style;
    s.pointerEvents = 'none';
    s.position = 'fixed';
    s.top = '0';
    s.left = '0';
    s.zIndex = '9999';

    this._resize();

    on(window, 'resize', this._onResize, { passive: true });
    on(window, 'orientationchange', this._onResize, { passive: true });
    on(document, 'visibilitychange', this._onVisibility);
    on(this.canvas, 'contextlost', this._onContextLost);
    on(this.canvas, 'contextrestored', this._onContextRestored);

    this._bindPointer();

    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', this._onMotionChange);
    }
  };

  Sparkles.prototype._enabled = function () {
    // No cursor trail for reduced-motion users or touch-only devices.
    return !motionQuery.matches && hoverQuery.matches;
  };

  Sparkles.prototype._bindPointer = function () {
    off(document, 'mousemove', this._onMouseMove);
    if (this._enabled()) {
      on(document, 'mousemove', this._onMouseMove, { passive: true });
    } else {
      this.particles.length = 0;
      this._stop();
      this._clear();
    }
  };

  Sparkles.prototype._resize = function () {
    if (!this.ctx) return;
    var dpr = Math.min(window.devicePixelRatio || 1, CONFIG.maxDpr);
    // Canvas is position:fixed, so the viewport is all it can ever display.
    var w = window.innerWidth;
    var h = window.innerHeight;
    if (w === this.w && h === this.h && dpr === this.dpr) return;

    this.w = w;
    this.h = h;
    this.dpr = dpr;

    this.canvas.width = Math.max(1, Math.round(w * dpr));
    this.canvas.height = Math.max(1, Math.round(h * dpr));
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    // Assigning width/height resets context state, so set the transform after.
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  Sparkles.prototype._onResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () {
      if (!self.destroyed) self._resize();
    }, CONFIG.resizeDebounceMs);
  };

  Sparkles.prototype._onVisibility = function () {
    if (document.hidden) {
      this._stop();
      this.particles.length = 0;
      this._clear();
    }
  };

  Sparkles.prototype._onContextLost = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    this.contextLost = true;
    this._stop();
    this.particles.length = 0;
  };

  Sparkles.prototype._onContextRestored = function () {
    this.contextLost = false;
    try {
      this.ctx = this.canvas.getContext('2d');
    } catch (err) {
      this.ctx = null;
      return;
    }
    this.w = this.h = this.dpr = 0; // force a rebuild
    this._resize();
  };

  Sparkles.prototype._clear = function () {
    if (this.ctx && this.w > 0 && this.h > 0) this.ctx.clearRect(0, 0, this.w, this.h);
  };

  Sparkles.prototype._onMouseMove = function (event) {
    if (this.destroyed || this.contextLost || document.hidden) return;

    var now = performance.now();
    if (now - this.lastEmit < CONFIG.emitIntervalMs) return;
    this.lastEmit = now;

    if (this.particles.length >= CONFIG.maxParticles) return;

    var x = event.clientX;
    var y = event.clientY;
    var half = CONFIG.spread * 0.5;

    for (var i = 0; i < CONFIG.perEmit; i++) {
      this.particles.push({
        x: x + (Math.random() * CONFIG.spread - half),
        y: y + (Math.random() * CONFIG.spread - half),
        r: Math.random() * 2,
        opacity: 1,
        // Floored, so every particle dies within ~17-50 frames.
        fade: CONFIG.fadeMin + Math.random() * (CONFIG.fadeMax - CONFIG.fadeMin),
        drift: Math.random() < 0.5 ? -Math.random() : Math.random()
      });
    }

    this._start();
  };

  Sparkles.prototype._start = function () {
    if (this.running || this.destroyed || this.contextLost) return;
    if (!this._enabled()) return;
    this.running = true;
    this.lastFrame = performance.now();
    this.rafId = requestAnimationFrame(this._frame);
  };

  Sparkles.prototype._stop = function () {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  };

  Sparkles.prototype._frame = function (now) {
    if (this.destroyed || !this.running || !this.ctx) return;

    // Normalise to a 60fps frame, clamped so a long task or a backgrounded
    // tab cannot produce a large jump when the page regains focus.
    var delta = now - this.lastFrame;
    this.lastFrame = now;
    var f = Math.min(Math.max(delta, 0) / 16.667, 3);

    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);

    var list = this.particles;
    for (var i = list.length; i--; ) {
      var p = list[i];
      p.opacity -= p.fade * f;
      if (p.opacity <= 0) {
        list.splice(i, 1);
        continue;
      }
      p.y += f;
      p.x += p.drift * f;

      ctx.globalAlpha = p.opacity;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2, false);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = this.color;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Idle out rather than spinning on an empty array.
    if (!list.length) {
      this.running = false;
      this.rafId = null;
      return;
    }

    this.rafId = requestAnimationFrame(this._frame);
  };

  Sparkles.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this._stop();
    clearTimeout(this.resizeTimer);
    this.particles.length = 0;
    this._clear();
    off(window, 'resize', this._onResize);
    off(window, 'orientationchange', this._onResize);
    off(document, 'visibilitychange', this._onVisibility);
    off(document, 'mousemove', this._onMouseMove);
    off(this.canvas, 'contextlost', this._onContextLost);
    off(this.canvas, 'contextrestored', this._onContextRestored);
    if (typeof motionQuery.removeEventListener === 'function') {
      motionQuery.removeEventListener('change', this._onMotionChange);
    }
  };

  // ---------------------------------------------------------------- bootstrap

  var instance = null;

  function mount() {
    try {
      var canvas = document.getElementById(CANVAS_ID);
      if (!canvas) return;
      if (instance && !instance.destroyed && instance.canvas === canvas) return;
      if (instance) instance.destroy();
      instance = new Sparkles(canvas);
      window.__vhSparkleCursor = instance;
    } catch (e) {
      if (window.console && console.warn) console.warn('sparkle-cursor: init failed', e);
    }
  }

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }

  // Theme editor can replace the canvas when a section re-renders.
  on(document, 'shopify:section:load', mount);
  on(document, 'shopify:section:unload', mount);

  window.Sparkles = Sparkles;
})();
